// Web Application page

import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { AlertTriangle, Settings, ShieldCheck, ShieldOff, Square } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import VerifyButton from "@/components/common/VerifyButton";
import PentestCard from "@/components/webApplication/PentestCard";
import TestDetails from "@/components/webApplication/TestDetails";
import VulnerabilityTable from "@/components/webApplication/VulnerabilityTable";
import WebApplicationClass, { LIVE_SIGNAL_SOURCE } from "@/models/webApplication";
import {
  BILLABLE_TEST_MIN_MINUTES,
  SECONDS_PER_MINUTE,
  EMPTY_STRING,
  IN_FLIGHT_TEST_STATUSES,
  TERMINAL_TEST_STATUSES,
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_STOPPING,
} from "../../constants/definitions";
import { deriveLiveTestState, isWebAppTestRunning } from "@/utils/liveTestState";
import { getSingleWebApplication } from "../../services/dataListOperations.service";
import {
  eventBus,
  NEW_TEST,
  TEST_STATUS_UPDATE,
  VULN_STATUS_UPDATE,
  WEBAPP_UPDATE,
} from "../../services/eventBus.service";
import ReportModal from "@/components/webApplication/ReportModal";
import { openReportInNewTab } from "@/utils/generateReport";
import ShowSettings from "@/components/webApplication/ShowSettings";
import UserClass from "@/models/user";
import { withRouter, WithRouterProps } from "@/router/withRouter";
import CognitoLoginClass from "../../models/cognitoLogin";
import TestResultsClass from "@/models/testResult";

// Stop / Start gating is now driven by the live testStatus (Provisioning,
// Running, Stopping, terminal) rather than a 5-minute time window. The
// Start/Stop button is disabled in TestDetails for Provisioning and
// Stopping, so the only remaining decision in startTestCallback is which
// modal to show on a Stop click — which simplifies to "always confirm
// the stop" when the button is reachable.

// Default-selection helpers. Pure functions of the webapp instance, so they
// live here rather than in the component body — the effects that derive the
// selection list them as dependencies, and a body-scoped definition would be
// a new identity on every render, re-running those effects every time.
const findInFlightId = (instance: WebApplicationClass): string => {
  const inflight = instance.tests?.find(
    (test) => !!test.testStatus && IN_FLIGHT_TEST_STATUSES.has(test.testStatus),
  );
  return inflight?.id ?? EMPTY_STRING;
};

const findLatestTerminalId = (instance: WebApplicationClass): string => {
  const latest = instance.tests?.find(
    (test) => !!test.testStatus && TERMINAL_TEST_STATUSES.has(test.testStatus),
  );
  return latest?.id ?? EMPTY_STRING;
};

const findDefaultSelectedId = (instance: WebApplicationClass): string => {
  const inflightId = findInFlightId(instance);
  if (inflightId) return inflightId;
  if (isWebAppTestRunning(instance)) return EMPTY_STRING;
  return findLatestTerminalId(instance);
};

interface WebApplicationProps extends WithRouterProps {
  listOfWebapps: WebApplicationClass[];
  webappid: string;
  subscriptionClient: any;
  verifyApp: (webappid: string) => void;
  updateWebAppTestState: (...args: any) => void;
  updateWebAppStatus: (...args: any[]) => void;
  user: UserClass;
  cognitologin: CognitoLoginClass;
  clientName: string;
}

const WebApplication: React.FC<WebApplicationProps> = (props) => {
  const {
    listOfWebapps,
    webappid,
    subscriptionClient,
    verifyApp,
    updateWebAppTestState,
    updateWebAppStatus,
    user,
    cognitologin,
    clientName,
    params,
    navigate,
    location,
    searchParams,
    setSearchParams,
  } = props;

  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const getWebapp = useCallback(
    (targetWebappId: string): WebApplicationClass => {
      let webapp = new WebApplicationClass();
      if (listOfWebapps) {
        for (const existingWebapp of listOfWebapps) {
          if (existingWebapp.id === targetWebappId) {
            webapp = existingWebapp;
            break;
          }
        }
      } else {
        webapp = getSingleWebApplication(targetWebappId, subscriptionClient);
      }
      return webapp;
    },
    [listOfWebapps, subscriptionClient]
  );

  const resolvedWebappId = webappid || params.webappid || EMPTY_STRING;
  const initialWebapp = getWebapp(resolvedWebappId);

  const [webapp, setWebapp] = useState<WebApplicationClass>(initialWebapp);
  const [showSettings, setShowSettings] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showVerifyGuard, setShowVerifyGuard] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  // Whether stopping RIGHT NOW would avoid consuming a pentest. Evaluated when
  // the confirmation opens (not continuously) so the copy can't change under
  // the user mid-read; a run that crosses the threshold while the dialog sits
  // open is an edge the backend still settles correctly either way.
  const [stopIsFreeOfCharge, setStopIsFreeOfCharge] = useState(false);
  const [errormessage, setErrormessage] = useState(EMPTY_STRING);
  const [refreshKey, setRefreshKey] = useState(Date.now());
  const [testRunning, setTestRunning] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const vulnSectionRef = useRef<HTMLDivElement>(null);
  const [testId, setTestId] = useState(initialWebapp.tests[0]?.id || EMPTY_STRING);
  const [testSelected, setTestSelected] = useState<TestResultsClass>(initialWebapp.tests[0]);

  // selectedTestId — which test the user is inspecting (drives the
  // findings tiles, vuln table, and Report). Distinct from
  // testSelected, which is the latest test of any status and drives
  // the in-flight UI state (Pentest button, Testing Live badge).
  //
  // Default selection rules:
  //   - In-flight test present in webapp.tests → select it. The user
  //     lands on the live entry on relogin / navigation.
  //   - No in-flight present, but webapp.testRunning is true → defer.
  //     The dashboard preload only ships the latest terminal test;
  //     during an in-flight run the live test isn't in webapp.tests
  //     until backfillTestHistory completes (~500ms). If we picked
  //     the latest terminal here, the page would briefly render that
  //     test's vulns before backfill promotes the selection to the
  //     live entry — a confusing flicker of stale data. Returning
  //     EMPTY_STRING leaves the surfaces in their loading-friendly
  //     "no selection" state (zeroed tiles + "Test in progress"
  //     placeholder) until backfill resolves and the auto-promote
  //     logic sets the live entry as selected.
  //   - Otherwise → latest terminal.
  //
  // The three helpers live at module scope (above) so they are stable
  // identities: the effects that derive the selection depend on them, and a
  // per-render redefinition would make those effects re-run every render.
  const [selectedTestId, setSelectedTestId] = useState<string>(
    findDefaultSelectedId(initialWebapp),
  );

  // Id of a test whose FINAL results we failed to fetch after it reached a
  // terminal status (see TestResultsClass.hydrateTerminalResult). Non-null
  // means the surfaces below are showing whatever this session happens to
  // hold — the live stream's findings — rather than the backend's final set.
  // That's a materially different claim from "here are the results", so it
  // gets said out loud, with a way to try again.
  const [finalResultsUnavailableTestId, setFinalResultsUnavailableTestId] =
    useState<string | null>(null);
  const [isRetryingFinalResults, setIsRetryingFinalResults] = useState(false);

  // Reset the severity filter whenever the selected test changes —
  // picking a different test should start "All vulns" rather than
  // carry the previous test's filter over.
  useEffect(() => {
    setSeverityFilter(null);
  }, [testSelected?.id]);

  // Hydrate the selected test on demand. The backfill seeds the
  // history slim (id, dates, status) — full vulns + screenshots /
  // steps / recommendations aren't fetched until the user actually
  // picks the test from the TestSelector. setRefreshKey forces a
  // re-render once the data lands so the Findings tiles and vuln
  // table fill in.
  useEffect(() => {
    if (!selectedTestId) return;
    const target = webapp.tests?.find((test) => test.id === selectedTestId);
    if (!target || target.fullResultLoaded) return;
    target
      .getFullTestResult(target.id)
      .then(() => {
        setRefreshKey(Date.now());
      })
      .catch(() => {});
  }, [selectedTestId, webapp]);

  // Clicking a Critical / High / Medium / Low tile in TestDetails sets
  // the filter and scrolls the vuln table into view. If the user
  // re-clicks the active severity, toggle the filter off so they can
  // return to All vulns from the same gesture.
  const handleSeverityTileClick = useCallback(
    (severity: string) => {
      setSeverityFilter((current) => (current === severity ? null : severity));
      vulnSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    []
  );

  // Manually picking a test from the TestSelector dropdown is an
  // explicit signal that the user has chosen what to view.
  const handleTestSelectionChange = useCallback((id: string) => {
    setSelectedTestId(id);
  }, []);

  // User-driven retry of a failed terminal hydration. Same call the
  // automatic path makes, so there is one hydration routine and one
  // definition of "did we get the final results".
  const retryFinalResults = useCallback(async () => {
    const testId = finalResultsUnavailableTestId;
    if (!testId || isRetryingFinalResults) return;

    const target = webapp.tests?.find((test) => test.id === testId);
    if (!target) {
      // The test is no longer on the model (webapp switched, history
      // reloaded). Nothing to retry against, and the banner would be
      // describing something the user can't see — clear it.
      setFinalResultsUnavailableTestId(null);
      return;
    }

    setIsRetryingFinalResults(true);
    try {
      const didHydrate = await target.hydrateTerminalResult();
      setFinalResultsUnavailableTestId(didHydrate ? null : testId);
    } finally {
      setIsRetryingFinalResults(false);
      setRefreshKey(Date.now());
    }
  }, [finalResultsUnavailableTestId, isRetryingFinalResults, webapp]);

  const selectTestById = useCallback(
    async (id: string) => {
      const test = webapp.tests.find((t) => t.id === id);
      if (!test) return;

      await test.getFullTestResult(id);
      webapp.tests = [...webapp.tests];

      setWebapp(webapp);
      setTestId(id);
      setTestSelected(test);
      setTestRunning(isWebAppTestRunning(webapp));
      setRefreshKey(Date.now());
    },
    [webapp]
  );

  // componentDidMount — root class
  useEffect(() => {
    const rootElement = document.getElementById("root");
    if (rootElement) rootElement.className = "root";
  }, []);

  // Number of tests the model currently holds for this webapp. Used as an
  // effect dependency so the two effects below re-run when data arrives
  // after mount — which is the normal case on a fresh login, where the
  // dashboard preload resolves some hundreds of milliseconds behind the
  // page's first render.
  const loadedTestCount = webapp.tests?.length ?? 0;

  // Which webapp we have already kicked a history load off for. A ref, not
  // state: it must not trigger a render, and it must be readable by the
  // effect on the same tick it is written.
  const historyLoadStartedForWebappIdRef = useRef(EMPTY_STRING);

  // Make sure the model actually holds this webapp's test history.
  //
  // This used to live in a mount-only effect, which meant its verdict was
  // decided by whatever the model happened to contain during the page's
  // first render. Land on /webapp/:id straight from login and that is
  // nothing at all: no tests, ownership not yet known. The effect then took
  // neither branch and never ran again, so an app with 148 tests behind it
  // rendered "Run your first pentest to see vulnerabilities here" — for the
  // rest of the visit. Keying on the webapp and its test count instead
  // means late-arriving data gets picked up.
  useEffect(() => {
    if (!webapp.id) return;
    if (historyLoadStartedForWebappIdRef.current === webapp.id) return;

    if (loadedTestCount > 0) {
      // Tests pre-loaded at login (slim projection from
      // listLatestTestsForDashboard). Backfill the rest of the history so
      // the TestSelector dropdown lists prior tests too — the preload only
      // ships the latest terminal per webapp.
      //
      // Backfill can also surface an in-flight test the preload didn't ship
      // (its inFlight* fields carry the signal, not the row). The selection
      // effect below promotes to it once it lands.
      historyLoadStartedForWebappIdRef.current = webapp.id;
      webapp
        .backfillTestHistory()
        .then(() => {
          setWebapp(webapp);
          setTestRunning(isWebAppTestRunning(webapp));
          setRefreshKey(Date.now());
        })
        .catch(() => {});
      return;
    }

    if (webapp.ownershipVerified) {
      // No tests on the model: app added mid-session, cache missed, or the
      // preload hasn't landed. loadFullData fully hydrates the most recent
      // N tests — its getTests() populates the whole list, so no separate
      // backfill is needed after it.
      historyLoadStartedForWebappIdRef.current = webapp.id;
      webapp
        .loadFullData(subscriptionClient)
        .then(() => {
          setWebapp(webapp);
          setTestRunning(isWebAppTestRunning(webapp));
          setRefreshKey(Date.now());
        })
        .catch(() => {});
    }
    // Unverified and testless: nothing to load, and nothing to remember —
    // if either fact changes, this effect reconsiders.
  }, [webapp, webapp.id, webapp.ownershipVerified, loadedTestCount, subscriptionClient]);

  // Keep "which test is the page about" in step with the model.
  //
  //   testSelected   — the latest attempt of any status. Drives the Pentest
  //                    button and the Testing Live badge.
  //   selectedTestId — the test being inspected. Drives every data surface.
  //
  // Both were previously derived once, at mount. Any test that arrived
  // afterwards — from the preload, the backfill, or a run started in
  // another session — left them pointing at nothing, and no later render
  // reconsidered. A selection the user made by hand is never overridden;
  // we only fill in a selection that is missing or has gone stale (the
  // webapp changed under us, or the history reloaded and the id is gone).
  useEffect(() => {
    if (!webapp.id || loadedTestCount === 0) return;

    const latestTest = webapp.tests[0];
    if (latestTest && latestTest.id !== testSelected?.id) {
      setTestId(latestTest.id || EMPTY_STRING);
      setTestSelected(latestTest);
    }
    setTestRunning(isWebAppTestRunning(webapp));

    const selectionStillExists =
      !!selectedTestId && webapp.tests.some((test) => test.id === selectedTestId);
    if (selectionStillExists) return;

    // findDefaultSelectedId deliberately returns empty during the window
    // where a run is live but its row hasn't reached webapp.tests yet —
    // leaving the surfaces in their "Test in progress" state rather than
    // flashing the previous test's findings. Respect that by only setting
    // a non-empty answer.
    const defaultId = findDefaultSelectedId(webapp);
    if (defaultId) setSelectedTestId(defaultId);
  }, [webapp, webapp.id, loadedTestCount, selectedTestId, testSelected?.id]);

  // componentDidUpdate — react to webappid prop/param changes.
  //
  // Adopting the new instance is all this does now. Loading its history,
  // promoting testSelected and picking a default selection are the job of
  // the two effects above, which key on the webapp and its test count and
  // therefore run for the newly adopted instance as well. They used to be
  // duplicated here, with the copies racing the originals whenever both
  // fired — two writers for one piece of state, differing in the edge
  // cases each remembered to handle.
  // Adopt on IDENTITY, not just on a changed id. The app list is rebuilt from
  // scratch on every login/session restore, and the rebuild produces a new
  // WebApplicationClass per app under the same id. Only the instances in that
  // list carry the live subscriptions and the in-flight resync, so a page
  // still holding a superseded instance renders a model nothing updates —
  // which is how a running pentest sat behind a disabled "Starting…" button
  // until the user logged out and back in.
  useEffect(() => {
    const nextId = webappid || params.webappid;
    if (!nextId) return;

    // Read the list directly rather than through getWebapp: its fallbacks
    // construct a webapp when the list can't answer, and constructing one per
    // effect run would never settle. Nothing to adopt is a legitimate answer —
    // keep the instance we have (the mount-time lookup already dealt with the
    // list being absent) and adopt when the list can name one.
    const currentWebapp = listOfWebapps?.find(
      (candidate) => candidate.id === nextId,
    );
    // Terminates: adopting makes the next run's lookup return the very
    // instance we just adopted, and the comparison then holds.
    if (currentWebapp && currentWebapp !== webapp) {
      setWebapp(currentWebapp);
      setRefreshKey(Date.now());
    }
  }, [webappid, params.webappid, webapp, listOfWebapps]);

  // eventBus subscribe/unsubscribe
  useEffect(() => {
    const handleTestUpdate = (payload?: {
      previousId?: string;
      currentId?: string;
    }) => {
      webapp.tests = [...webapp.tests];
      const newest = webapp.tests[0];
      // Always sync testSelected to the latest test so the in-flight
      // UI state (Pentest button, Testing Live badge, rotating status
      // message) flips immediately even when the test is still an
      // optimiztic placeholder with a locally-generated id.
      setTestSelected(newest);
      setTestRunning(isWebAppTestRunning(webapp));

      // selectedTestId migration. Two cases:
      //
      //   (a) Id swap from startTest — payload carries
      //       { previousId, currentId } describing the placeholder-
      //       to-real-id rename. If the user's selection was pinned to
      //       the placeholder, migrate to the real id; if they had
      //       manually picked something else in the brief in-flight
      //       window, preserve their choice.
      //
      //   (b) Plain emit (no payload) — typically the initial create
      //       on Pentest Now. Auto-select the newest test so the data
      //       surfaces follow the run the user just started.
      if (payload?.previousId && payload?.currentId) {
        setSelectedTestId((current) =>
          current === payload.previousId ? payload.currentId! : current,
        );
      } else if (newest?.id) {
        setSelectedTestId(newest.id);
      }

      if (newest?.id && !newest.fullResultLoaded) {
        selectTestById(newest.id);
      } else {
        setWebapp(webapp);
        setRefreshKey(Date.now());
      }
    };

    // Test status changed. Three jobs:
    // 1. Sync local testRunning + force re-render so the Pentest button
    //    and Testing Live badge reflect the new state.
    // 2. If the event targets testSelected (the latest test attempt),
    //    refresh its reference so React picks up the updated fields.
    // 3. If the transition is INTO a terminal state, this test just
    //    became "current state". Promote the selectedTestId silently if
    //    the user was on the previously-latest terminal; leave a
    //    manually-picked older test alone. The previously-latest
    //    terminal is captured before the new status takes effect; we
    //    compare selectedTestId against that.
    const handleTestStatusUpdate = (payload?: { testid?: string }) => {
      // Latest terminal id *excluding* the test that just changed.
      // The model has already mutated matchingTest.testStatus in
      // place by the time this handler runs, so a naive
      // findLatestTerminalId would include the just-promoted test
      // and report it as both the previous and the new latest —
      // making isNewTerminal always false and silently breaking the
      // auto-promote branch. Excluding payload.testid
      // gives us the terminal state as it was *before* this event.
      const previousLatestTerminalId =
        (webapp.tests ?? [])
          .filter(
            (test) =>
              test.id !== payload?.testid &&
              !!test.testStatus &&
              TERMINAL_TEST_STATUSES.has(test.testStatus),
          )
          .sort(
            (a, b) => Number(b.startTime ?? 0) - Number(a.startTime ?? 0),
          )[0]?.id ?? "";

      setTestRunning(isWebAppTestRunning(webapp));
      if (payload?.testid && testSelected?.id === payload.testid) {
        const refreshedTest = webapp.tests.find(
          (test) => test.id === payload.testid
        );
        if (refreshedTest) setTestSelected(refreshedTest);
      }

      if (payload?.testid) {
        const updatedTest = webapp.tests.find(
          (test) => test.id === payload.testid
        );
        const becameTerminal =
          !!updatedTest?.testStatus &&
          TERMINAL_TEST_STATUSES.has(updatedTest.testStatus);
        const isNewTerminal =
          becameTerminal && updatedTest.id !== previousLatestTerminalId;

        if (isNewTerminal) {
          // Refetch on terminal transition — the BE now has the final vuln
          // set for this test, and until it lands the page is showing this
          // session's own record of the run. hydrateTerminalResult retries a
          // bounded number of times and reports whether it got a genuinely
          // terminal response; a failure is surfaced to the user rather than
          // swallowed, because the alternative is presenting a partial (or,
          // before the getDisplayVulns fix, empty) view as if it were the
          // final result. setRefreshKey re-renders so the Findings tiles +
          // vuln table pick up the hydrated data.
          //
          // Skipped once we already hold the final set: terminal events can
          // repeat (the subscription and the 30s resync can both report the
          // same transition), and each repeat would otherwise re-run the
          // whole retry sequence against data that cannot change.
          if (!updatedTest.terminalHydrationComplete) {
            setFinalResultsUnavailableTestId(null);
            updatedTest
              .hydrateTerminalResult()
              .then((didHydrate) => {
                setFinalResultsUnavailableTestId(
                  didHydrate ? null : updatedTest.id,
                );
                setRefreshKey(Date.now());
              })
              .catch(() => {
                setFinalResultsUnavailableTestId(updatedTest.id);
                setRefreshKey(Date.now());
              });
          }

          const userIsAlreadyOnNewTerminal = selectedTestId === updatedTest.id;
          const userWasOnPreviousLatest =
            selectedTestId === previousLatestTerminalId;

          if (userIsAlreadyOnNewTerminal) {
            // Selection already follows this test — typically because
            // the NEW_TEST handler auto-selected the in-flight test
            // when the user clicked Pentest Now, and it has now
            // reached terminal. No selection change needed; the refetch
            // above triggers the re-render that fills in the data.
          } else if (userWasOnPreviousLatest || !selectedTestId) {
            // User was tracking the previously-latest terminal (or
            // had no selection). Silently promote to the new one.
            setSelectedTestId(updatedTest.id);
          }
          // A manually-picked older test is left exactly where it is.
          // The new run is already in the TestSelector dropdown, at the
          // top, which is how the user reaches it.
        }
      }

      setRefreshKey(Date.now());
    };

    // Vuln status changed (Open → Fixed, etc.) for a vuln on this webapp.
    // Force a re-render so the vuln table picks up the new status.
    const handleVulnStatusUpdate = () => {
      setRefreshKey(Date.now());
    };

    // WebApp record was updated (settings, ownership, schedule, etc.).
    // Refresh local webapp state and force a re-render.
    const handleWebappUpdate = () => {
      setWebapp(webapp);
      setRefreshKey(Date.now());
    };

    eventBus.on(NEW_TEST, handleTestUpdate);
    eventBus.on(TEST_STATUS_UPDATE, handleTestStatusUpdate);
    eventBus.on(VULN_STATUS_UPDATE, handleVulnStatusUpdate);
    eventBus.on(WEBAPP_UPDATE, handleWebappUpdate);
    return () => {
      eventBus.off(NEW_TEST, handleTestUpdate);
      eventBus.off(TEST_STATUS_UPDATE, handleTestStatusUpdate);
      eventBus.off(VULN_STATUS_UPDATE, handleVulnStatusUpdate);
      eventBus.off(WEBAPP_UPDATE, handleWebappUpdate);
    };
  }, [webapp, selectTestById, testSelected]);

  // Cleanup error timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  // Live findings stream. While the selected test is in-flight, subscribe to
  // the slim onVulnerabilityDiscovered events so the "Findings so far — test in
  // progress" section (in TestDetails) fills in real time. Findings land on
  // testSelected.liveFindings and TestDetails re-renders on LIVE_FINDINGS_UPDATE;
  // the authoritative Open-Findings tiles + dashboard cards are untouched. Torn
  // down when the test terminates or the selection/route changes.
  useEffect(() => {
    const test = testSelected;
    if (!test || !subscriptionClient || !test.isLive) return undefined;
    // Catching matters: a failed open (no auth token, socket down) would
    // otherwise surface as an unhandled rejection. The model records the
    // intent regardless, so the in-flight resync retries the open.
    test.subscribeToVulns(subscriptionClient, webappid).catch((subscribeError) => {
      console.error(
        `Failed to open the live findings stream for test ${test.id}:`,
        subscribeError,
      );
    });
    return () => {
      test.unsubscribeFromVulns();
    };
  }, [testSelected, testSelected?.id, testSelected?.isLive, subscriptionClient, webappid]);


  /* ---- Event handlers ---- */

  const appVerifiedSuccess = useCallback(() => {
    webapp.ownershipVerified = true;
    setWebapp(webapp);
    verifyApp(webapp.id);
  }, [webapp, verifyApp]);

  // Proceed with starting or stopping a test after all guards have passed.
  //
  // The single most important rule here: branch on the user's INTENT (what
  // they meant when they clicked the button), not on the post-sync local
  // state. Previously this function inferred intent from the local
  // testRunning flag after reconciling it with the backend, which meant
  // a Stop click on a test that had already ended on the backend would
  // collapse to "not running locally + not running on backend" and
  // erroneously trigger a new startTest(). That's how a Stop click ended
  // up provisioning a new cluster against a still-deleting stack.
  // retestOnly was an FE concept that triggered a targeted retest run.
  // The retest affordance was removed from the UI in v1 (see the v1
  // vuln-state-model brief); the backend retains the code path for
  // Phase 2. Calls into the model always pass retestOnly = false now.
  const executeTestAction = useCallback(async () => {
    // Capture intent up front — frozen for the rest of this call.
    const userWantsToStop = isWebAppTestRunning(webapp);

    // Optimiztic live-signal flip for snappy same-frame feedback,
    // expressed through the single setter: Stop → Stopping, Start →
    // Provisioning. startTest()/stopTest() re-assert the same activity
    // (idempotent), and the branches below only ever act on
    // userWantsToStop, so a backend answer that contradicts our optimizm
    // is adopted without changing intent.
    if (userWantsToStop) {
      const liveTest = webapp.tests.find((test) => test.isLive);
      webapp.setTestActivity(
        {
          testId: liveTest?.id ?? webapp.testActivity?.testId ?? null,
          testStatus: TEST_STATUS_STOPPING,
          startTime: webapp.testActivity?.startTime ?? null,
        },
        { source: LIVE_SIGNAL_SOURCE.stopClick },
      );
    } else {
      // startsNewRun: the click IS the new run, so Provisioning here is a
      // forward step and must not be read as a rewind of any signal a
      // previous run left behind.
      webapp.setTestActivity(
        {
          testId: null,
          testStatus: TEST_STATUS_PROVISIONING,
          startTime: Math.floor(Date.now() / 1000),
        },
        { source: LIVE_SIGNAL_SOURCE.startClick, startsNewRun: true },
      );
    }
    setErrormessage(EMPTY_STRING);
    setTestRunning(isWebAppTestRunning(webapp));
    setWebapp(webapp);

    try {
      if (userWantsToStop) {
        // STOP path keeps the isTestRunning safety check: stopping a
        // test that already ended on the backend would otherwise fire
        // a wasted mutation and surface a noisy "test not running"
        // error.
        const { isRunning } = await webapp.isTestRunning();
        if (isRunning) {
          // Honour the user's intent — issue the stop. testRunning stays
          // true through Stopping; the backend holds the terminal
          // onTestStatusChange event until teardown completes, so
          // flipping testRunning false here would re-enable Start
          // mid-teardown. Set the running test's status to Stopping
          // locally so the UI flips to the disabled "Stopping…" state
          // immediately, then fire the mutation.
          const runningTest = webapp.tests.find((test) => test.isLive);
          if (runningTest) {
            runningTest.testStatus = TEST_STATUS_STOPPING;
          }
          const stopResult = await webapp.stopTest();
          if (!stopResult.success) {
            // Backend rejected the stop (e.g. status was already
            // transitioning, or a stale tab clicked Stop after teardown
            // already started). Roll back the optimiztic Stopping state
            // and surface the backend's message so the user knows why.
            // Clearing the activity mirrors the prior behavior (idle
            // button); the next subscription/resync re-asserts the real
            // phase if the run is in fact still going.
            if (runningTest && runningTest.testStatus === TEST_STATUS_STOPPING) {
              runningTest.testStatus = null;
            }
            webapp.setTestActivity(null, {
              source: LIVE_SIGNAL_SOURCE.stopRejected,
            });
            if (stopResult.message) {
              setErrormessage(stopResult.message);
            }
          }
        } else {
          // Backend already stopped on its own (e.g. infra failure, or
          // the websocket lagged behind). Adopt the backend state; do
          // not issue any further action.
          webapp.setTestActivity(null, {
            source: LIVE_SIGNAL_SOURCE.stopAlreadyEnded,
          });
        }
      } else {
        // START path: call straight through, no isTestRunning
        // precheck. The precheck was the source of the visible
        // latency between click and "Testing Live" state — every
        // start paid two BE round-trips. Skipping it lets
        // webapp.startTest fire NEW_TEST synchronously (with a
        // locally-generated placeholder id) so the UI flips into
        // in-flight state on the same frame as the click. The BE
        // mutation runs immediately after; if it rejects, startTest
        // cleans up its own placeholder and reconciles testRunning
        // against actual BE state (which may still be true if
        // another admin's test is already running on this webapp).
        const result = await webapp.startTest(subscriptionClient, false);
        if (!result.success && result.message) {
          setErrormessage(result.message);
        }
      }

      webapp.lastStartButtonClickTime = new Date();
      updateWebAppTestState(webapp.id, webapp.lastStartButtonClickTime);

      setTestRunning(isWebAppTestRunning(webapp));
      setWebapp(webapp);
    } catch {
      setErrormessage("Error checking test state.");
    }
  }, [webapp, subscriptionClient, updateWebAppTestState]);

  const startTestCallback = useCallback(() => {
    if (!webapp.ownershipVerified) {
      setShowVerifyGuard(true);
      return;
    }

    // The Stop button is only reachable when the test is in Running
    // state — TestDetails disables it during Provisioning ("Starting…")
    // and Stopping ("Stopping…"). So a Stop click here always means a
    // user intent to stop a fully-running test; surface the confirmation
    // modal directly.
    if (isWebAppTestRunning(webapp)) {
      // Mirrors the BE's billable rule (quota.is_billable_test): a stop only
      // consumes a pentest once the run has passed the billable threshold.
      // Display-only — the backend decides what is actually charged.
      const testStartedAtEpoch = webapp.testActivity?.startTime ?? null;
      const elapsedSeconds = testStartedAtEpoch
        ? Math.floor(Date.now() / 1000) - testStartedAtEpoch
        : null;
      setStopIsFreeOfCharge(
        elapsedSeconds !== null && elapsedSeconds <= BILLABLE_TEST_MIN_MINUTES * SECONDS_PER_MINUTE
      );
      setShowStopConfirm(true);
      return;
    }

    // Production-risk consent is captured up-front in the Add
    // Application wizard (Acknowledgment step) and persisted on the
    // webapp record via createWebApplication. We no longer block the
    // first test with a separate modal.
    executeTestAction();
  }, [webapp, executeTestAction]);

  // Called when the user confirms the stop modal
  const handleStopConfirm = useCallback(() => {
    setShowStopConfirm(false);
    executeTestAction();
  }, [executeTestAction]);

  const resetErrorString = useCallback(() => {
    setErrormessage(EMPTY_STRING);
    clearTimeout(errorTimeoutRef.current);
  }, []);

  const toggleShowSettings = useCallback((..._args: any[]) => {
    setShowSettings((prev) => !prev);
  }, []);

  /* ---- Derive the vuln-display test ----
   *
   * `testSelected` is the latest test attempt (any status). It drives
   * the test-state UI: the Pentest button, the Testing Live badge,
   * the in-flight rotating status message.
   *
   * `selectedTest` is the specific terminal test the user is
   * inspecting (controlled via TestSelector in the Security Status
   * header). It drives every data surface on the page — Findings
   * tiles, vuln table, and Report generation. Defaults to the latest
   * terminal test on mount; the user can switch to any prior terminal
   * via the dropdown.
   *
   * When a new test reaches terminal while the page is open and the
   * user is still on the previously-latest terminal, we auto-promote
   * the selection so they see the fresh data. If the user has manually
   * picked an older test, we keep their selection and say nothing —
   * the new run is at the top of the TestSelector dropdown, which is a
   * quieter and more reliable place to find it than a banner whose
   * appearance depended on the order events happened to arrive in.
   *
   * Failed / Invalid tests are terminal too — they're in the dropdown
   * and selectable. The red banner inside TestDetails communicates
   * the partial nature of those runs; their findings are still
   * surfaced as current state (per FailedTestFindings BE brief).
   */
  // An EMPTY selectedTestId is honoured as-is — no fallback to the latest
  // terminal. That would defeat the deferred-selection design: when a run is
  // live but its row hasn't reached webapp.tests yet, the id is deliberately
  // empty so the page shows "Test in progress" rather than flashing the
  // prior terminal's vulns.
  //
  // A selectedTestId belonging to a DIFFERENT webapp is a different matter:
  // it's stale state from before a navigation, and rendering nothing for it
  // would blank the page for the frame between adopting the new webapp and
  // the effect above re-deriving the selection. Fall back to this webapp's
  // default for that frame; the effect then persists the same answer.
  const selectionBelongsToThisWebapp =
    !selectedTestId ||
    !!webapp.tests?.some((test) => test.id === selectedTestId);
  const effectiveSelectedTestId = selectionBelongsToThisWebapp
    ? selectedTestId
    : findDefaultSelectedId(webapp);
  const selectedTest = effectiveSelectedTestId
    ? webapp.tests?.find((test) => test.id === effectiveSelectedTestId)
    : undefined;

  /* ---- Render ---- */

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col gap-6">

        {/* Page header */}
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            {/* App name + verification status inline */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-2xl font-semibold text-gray-900 truncate">{webapp.name}</h1>
              {webapp.ownershipVerified ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 shrink-0">
                  <ShieldCheck className="h-3 w-3" />
                  Verified
                </span>
              ) : (
                <VerifyButton
                  webappid={webapp.id}
                  webapp={webapp}
                  appVerifiedSuccess={appVerifiedSuccess}
                />
              )}
            </div>
            <p className="text-sm text-gray-500 truncate mt-0.5">{webapp.url}</p>
            {errormessage && (
              <p className="text-sm text-red-600 mt-1">{errormessage}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Report has moved into the Security Status card header,
                stacked beneath the TestSelector dropdown so it's
                visually paired with the test it generates against.
                Settings stays at the page level — it's app-scoped,
                not test-scoped. */}
            <Button
              variant="outline"
              size="sm"
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                setShowSettings((prev) => !prev);
              }}
              className="gap-1.5"
            >
              <Settings className="h-3.5 w-3.5" />
              Settings
            </Button>
          </div>
        </div>

        {/* Final results couldn't be fetched for the test being viewed.
            Shown only while that test is the selected one — the findings
            below are then this session's live-stream record, which may be
            incomplete. Amber, not red: nothing is broken about the test
            itself, we just couldn't read its final state back. */}
        {finalResultsUnavailableTestId &&
          effectiveSelectedTestId === finalResultsUnavailableTestId && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3"
            >
              <AlertTriangle
                className="h-4 w-4 shrink-0 text-amber-500"
                aria-hidden="true"
              />
              <p className="flex-1 text-sm text-amber-800">
                Couldn't load the final results for this test. The findings
                below are what was recorded during the run and may be
                incomplete.
              </p>
              <button
                type="button"
                onClick={retryFinalResults}
                disabled={isRetryingFinalResults}
                className="text-xs font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900 disabled:opacity-50"
              >
                {isRetryingFinalResults ? "Retrying…" : "Retry"}
              </button>
            </div>
          )}

        {/* Unified Pentest + Security Status card. Pentest action
            (1/4) sits on the left with a vertical divider separating
            it from the Security Status data (3/4) on the right —
            single white surface, two logical sections, one chrome.
            The divider is bottom-aligned on narrow viewports where
            the columns stack. testSelected drives the in-flight state
            UI in PentestCard (button + rotating status); selectedTest
            drives the Findings tiles in TestDetails. */}
        <Card className="overflow-hidden">
          <div className="flex flex-col lg:flex-row items-stretch divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
            <div className="lg:w-1/4 lg:shrink-0">
              <PentestCard
                webAppId={webapp.id}
                livePhase={deriveLiveTestState(webapp).phase}
                startTestCallback={startTestCallback}
              />
            </div>
            <div className="lg:flex-1 min-w-0">
              <TestDetails
                webapp={webapp}
                testSelected={testSelected}
                displayTest={selectedTest}
                testRunning={isWebAppTestRunning(webapp)}
                severityFilter={severityFilter}
                onSeverityClick={handleSeverityTileClick}
                selectedTestId={effectiveSelectedTestId}
                onTestSelectionChange={handleTestSelectionChange}
                onOpenReport={() => setShowReport(true)}
              />
            </div>
          </div>
        </Card>

        {/* Vulnerabilities table — sourced from selectedTest (the
            terminal test the user picked in the TestSelector). testId
            follows the same so row-level navigation points to the
            right test record. */}
        <Card ref={vulnSectionRef} className="scroll-mt-20">
          <VulnerabilityTable
            webapp={webapp}
            testSelected={selectedTest as TestResultsClass}
            testId={selectedTest?.id ?? testId}
            severityFilter={severityFilter}
            onSeverityFilterChange={setSeverityFilter}
          />
        </Card>
      </div>

      {/* Verify guard modal */}
      {showVerifyGuard && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowVerifyGuard(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-sm rounded-xl bg-white shadow-xl"
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            <div className="flex flex-col items-center gap-3 px-6 py-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
                <ShieldOff className="h-6 w-6 text-amber-500" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Ownership not verified</h2>
              <p className="text-sm text-gray-500">
                You need to verify ownership of this application before starting a test. Use the <span className="font-medium text-gray-700">Verify</span> button next to the app name.
              </p>
            </div>
            <div className="flex justify-center px-6 pb-6">
              <Button size="sm" onClick={() => setShowVerifyGuard(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Stop test confirmation modal */}
      {showStopConfirm && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowStopConfirm(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-sm rounded-xl bg-white shadow-xl"
            onClick={(clickEvent) => clickEvent.stopPropagation()}
          >
            <div className="flex flex-col items-center gap-3 px-6 py-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <Square className="h-6 w-6 text-red-500" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Stop Test?</h2>
              <p className="text-sm text-gray-500">
                Stopping early will produce incomplete findings.{" "}
                {/* The reassurance is shown ONLY while it is actually true.
                    Past the billable threshold this stop DOES consume a pentest,
                    so promising otherwise would be a false billing statement —
                    the copy falls back to saying nothing about usage rather than
                    something wrong. The threshold itself is never named. */}
                {stopIsFreeOfCharge
                  ? "Mistakes happen — if you need to stop now, this test won't count towards your usage."
                  : "This test has been running long enough to count towards your usage."}
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 pb-6">
              <Button variant="outline" size="sm" onClick={() => setShowStopConfirm(false)}>
                Keep Running
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleStopConfirm}
              >
                Stop Test
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ReportModal
        webapp={webapp}
        show={showReport}
        close={() => setShowReport(false)}
        onGenerate={(reportType, _password) => {
          // Report generates against the user-selected terminal test
          // (TestSelector value) — not the latest test in the array.
          // That way picking an older test in the dropdown lets the
          // user export the report for that specific historical run.
          if (selectedTest) {
            openReportInNewTab(webapp, selectedTest, selectedTest.vulns || [], clientName, reportType === "redacted");
          }
          setShowReport(false);
        }}
      />
      <ShowSettings
        webapp={webapp}
        show={showSettings}
        subscriptionClient={subscriptionClient}
        close={toggleShowSettings}
      />

    </div>
  );
};

export default withRouter(WebApplication);
